import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowRight, ClipboardList, Users, ShieldCheck, Gift, CheckCircle2, Briefcase, Star, Hammer, Wrench, Zap, PaintRoller, Home, Grid3x3, Sprout, SprayCan, Truck, Blocks, LayoutGrid, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api from "@/api";
import { CraftsmanCard } from "@/components/CraftsmanCard";
import { Pricing } from "@/components/Pricing";
import { LegalNotice } from "@/components/LegalNotice";
import { PromoBanner } from "@/components/PromoBanner";

const catIcons = {
  carpentry: Hammer, plumbing: Wrench, electrical: Zap, painting: PaintRoller,
  roofing: Home, tiling: Grid3x3, gardening: Sprout, cleaning: SprayCan, moving: Truck, masonry: Blocks,
};

const tilePalette = ["#C65338", "#5C6646", "#D68A12", "#2B3A5E"];

export default function Landing() {
  const { t, catName } = useI18n();
  const nav = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/craftsmen").then((r) => setFeatured(r.data.slice(0, 3)));
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash]);

  const search = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (city) p.set("city", city);
    nav(`/craftsmen?${p.toString()}`);
  };

  return (
    <div>
      <PromoBanner />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-worker.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/90 via-stone-900/70 to-stone-900/30" />
          <div className="absolute inset-0 hero-grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <div data-testid="hero-free-badge" className="inline-flex items-center gap-2 rounded-full bg-[#5C6646] text-white px-4 py-1.5 text-sm font-bold mb-4 shadow-lg"><CheckCircle2 className="w-4 h-4" />{t("free.badge")}</div>
            <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#E69625] mb-5">{t("hero.eyebrow")}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-white">{t("hero.title")}</h1>
            <p className="mt-6 text-base sm:text-lg text-stone-200 leading-relaxed max-w-xl">{t("hero.subtitle")}</p>

            <div className="mt-9 bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-2xl max-w-xl">
              <div className="flex-1 flex items-center gap-2 px-3">
                <Search className="w-5 h-5 text-[#A89A90] shrink-0" />
                <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder={t("hero.searchPlaceholder")} className="border-0 shadow-none focus-visible:ring-0 px-0 text-[#2D231F]" />
              </div>
              <div className="flex items-center gap-2 px-3 sm:border-l border-[#EBDDD3]">
                <MapPin className="w-5 h-5 text-[#A89A90] shrink-0" />
                <Input data-testid="hero-city-input" value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder={t("hero.cityPlaceholder")} className="border-0 shadow-none focus-visible:ring-0 px-0 text-[#2D231F] sm:w-32" />
              </div>
              <Button data-testid="hero-search-btn" onClick={search} className="rounded-xl bg-[#C65338] hover:bg-[#A93F25] text-white h-12 px-7 font-semibold">{t("hero.search")}</Button>
            </div>

            <div data-testid="hero-free-chips" className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white font-medium">
              {["free.chip1","free.chip2","free.chip3"].map(k => (<span key={k} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#9DB57A]" />{t(k)}</span>))}
            </div>
            <button onClick={() => nav(user ? "/post-job" : "/register?role=customer")} data-testid="hero-post-job" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#C65338] hover:bg-[#A93F25] text-white font-bold px-7 h-14 text-base shadow-lg transition-colors">
              {t("free.cta")} <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-3 bg-white rounded-2xl border border-[#EBDDD3] shadow-xl divide-x divide-[#EBDDD3] overflow-hidden">
          {[
            { n: t("val.v1n"), l: t("val.v1l"), Icon: Gift },
            { n: t("val.v2n"), l: t("val.v2l"), Icon: Handshake },
            { n: t("val.v3n"), l: t("val.v3l"), Icon: Users },
          ].map(({ n, l, Icon }, i) => (
            <motion.div key={l} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              data-testid={`stat-${i}`}
              className="flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3.5 py-6 sm:py-7 px-3 hover:bg-[#FBF3EC] hover:-translate-y-0.5 transition-all duration-300">
              <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#C65338] to-[#E69625] flex items-center justify-center shrink-0">
                <Icon className="w-6 h-6 text-white" />
              </span>
              <div className="text-center sm:text-left">
                <div className="font-display text-2xl sm:text-3xl font-black text-[#C65338] leading-none">{n}</div>
                <div className="text-xs sm:text-sm text-[#8C7A70] mt-1">{l}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Audience cards */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 relative z-10" data-testid="audience-cards">
        <div className="grid md:grid-cols-2 gap-5">
          {[
            { k: "customer", Icon: ClipboardList, color: "#C65338", title: t("aud.customerTitle"), line: t("aud.customerLine"), btn: t("aud.customerBtn"), steps: [t("aud.c1"), t("aud.c2"), t("aud.c3")], to: "/register?role=customer", id: "aud-customer-btn" },
            { k: "craft", Icon: Hammer, color: "#5C6646", title: t("aud.craftTitle"), line: t("aud.craftLine"), btn: t("aud.craftBtn"), steps: [t("aud.h1"), t("aud.h2"), t("aud.h3")], to: "/register?role=craftsman", id: "aud-craft-btn" },
          ].map(({ k, Icon, color, title, line, btn, steps, to, id }) => (
            <div key={k} className="bg-white rounded-2xl border border-[#EBDDD3] shadow-lg p-6 sm:p-8 flex flex-col">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: color + "1A" }}><Icon className="w-6 h-6" style={{ color }} /></span>
                <h2 className="font-display text-2xl font-black text-[#2D231F]">{title}</h2>
              </div>
              <p className="mt-3 text-[#8C7A70]">{line}</p>
              <ol className="mt-4 space-y-2 flex-1">
                {steps.map((s, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-[#2D231F]">
                    <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: color }}>{i + 1}</span>{s}
                  </li>
                ))}
              </ol>
              <Button data-testid={id} onClick={() => nav(user && user !== false ? (k === "customer" ? "/post-job" : "/jobs") : to)}
                className="mt-6 rounded-full text-white h-12 px-8 text-base font-semibold gap-2 self-start" style={{ background: color }}>
                {btn} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-[#EEF0E4] border-y border-[#DDE1CB]" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="mb-10">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#C65338] bg-[#C65338]/10 rounded-full px-3.5 py-1.5 mb-4">
            <LayoutGrid className="w-4 h-4" /> {t("categories.eyebrow")}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F]">{t("categories.title")}</h2>
          <p className="text-[#8C7A70] mt-2">{t("categories.subtitle")}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {cats.map((c, i) => {
            const Icon = catIcons[c] || Grid3x3;
            const col = tilePalette[i % tilePalette.length];
            return (
              <motion.button key={c} data-testid={`category-${c}`}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                onClick={() => nav(`/craftsmen?trade=${c}`)}
                className="group rounded-2xl border border-[#EBDDD3] bg-white p-6 text-left hover:border-[#C65338]/50 hover:shadow-lg hover:-translate-y-1 transition-all">
                <span className="w-14 h-14 rounded-full flex items-center justify-center mb-4 shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: col }}>
                  <Icon className="w-6 h-6 text-white" />
                </span>
                <div className="font-semibold text-[#2D231F]">{catName(c)}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs text-[#A89A90]">
                  {t("categories.find")}
                  <ArrowRight className="w-3.5 h-3.5 text-[#C65338] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
      </section>

      {/* How it works */}
      <section className="bg-[#F8E6DC] border-y border-[#EFCFC0]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] uppercase text-[#C65338] bg-[#C65338]/10 rounded-full px-3.5 py-1.5">
              {t("how.eyebrow")}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F] mt-5">{t("how.title")}</h2>
            <p className="mt-3 text-[#8C7A70] text-base md:text-lg leading-relaxed">{t("how.subtitle")}</p>
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#157F5B] bg-[#157F5B]/10 rounded-full px-4 py-2" data-testid="how-free-badge">
              <Gift className="w-4 h-4" /> {t("how.freeBadge")}
            </span>
          </div>

          <div className="relative mt-16">
            {/* connecting flow line (desktop) — sits at medallion centers, above the cards so it stays visible */}
            <div className="hidden md:block absolute top-[52px] left-[16.5%] right-[16.5%] border-t-2 border-dashed border-[#C65338]/40 z-0" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 relative z-10">
              {[
                { Icon: ClipboardList, k: "s1", color: "#C65338", tint: "#E07A5F" },
                { Icon: Users, k: "s2", color: "#5C6646", tint: "#7C8A5F" },
                { Icon: ShieldCheck, k: "s3", color: "#157F5B", tint: "#2FA87C" },
              ].map(({ Icon, k, color, tint }, i) => (
                <motion.div key={k} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.14, duration: 0.5 }}
                  data-testid={`how-step-${i + 1}`}
                  className="flex flex-col items-center text-center">
                  <div className="relative z-10">
                    <div className="w-[104px] h-[104px] rounded-full flex items-center justify-center shadow-lg ring-8 ring-[#F7F0E9]"
                      style={{ background: `linear-gradient(135deg, ${color}, ${tint})` }}>
                      <Icon className="w-11 h-11 text-white" strokeWidth={1.75} />
                    </div>
                    <span className="absolute -top-1 ltr:-right-1 rtl:-left-1 w-9 h-9 rounded-full bg-white flex items-center justify-center font-display font-black text-lg shadow-md ring-1 ring-black/5" style={{ color }}>
                      {i + 1}
                    </span>
                  </div>
                  <div className="mt-7 w-full bg-white rounded-3xl border border-[#EBDDD3] px-7 pt-7 pb-8 hover:-translate-y-1 hover:shadow-xl hover:border-[#C65338]/30 transition-[transform,box-shadow,border-color] duration-300">
                    <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color }}>{t("how.step")} {i + 1}</span>
                    <h3 className="font-display text-xl font-bold text-[#2D231F] mt-2 mb-3">{t(`how.${k}t`)}</h3>
                    <p className="text-[#8C7A70] text-sm leading-relaxed">{t(`how.${k}d`)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center gap-3">
            <Button data-testid="how-cta" onClick={() => nav("/register")} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-12 px-8 text-base font-semibold gap-2 shadow-lg shadow-[#C65338]/20">
              {t("how.cta")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Button>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#8C7A70]">
              <CheckCircle2 className="w-4 h-4 text-[#157F5B]" /> {t("how.freeBadge")}
            </span>
          </div>
        </div>
      </section>

      {/* Fair rules */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="fair-section">
        <div className="grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-2 relative rounded-3xl overflow-hidden h-56 md:h-72 shadow-lg">
            <img src="/fair-craftsman.jpg" alt="" loading="lazy" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-900/10 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-white font-semibold"><Handshake className="w-5 h-5 text-[#E69625]" />{t("fair.eyebrow")}</div>
          </div>
          <div className="md:col-span-3">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#5C6646] bg-[#5C6646]/10 rounded-full px-3.5 py-1.5 mb-4"><Handshake className="w-4 h-4" /> {t("fair.eyebrow")}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F]">{t("fair.title")}</h2>
            <p className="mt-3 text-[#6B5A50] text-base md:text-lg leading-relaxed">{t("fair.intro")}</p>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {[
            { id: "fair-customer", color: "#C65338", Icon: ClipboardList, title: t("fair.custTitle"), sub: t("fair.custSub"), keys: ["k1", "k2", "k3", "k4", "k5"] },
            { id: "fair-craftsman", color: "#5C6646", Icon: Hammer, title: t("fair.craftTitle"), sub: t("fair.craftSub"), keys: ["h1", "h2", "h3", "h4", "h5"] },
          ].map(({ id, color, Icon, title, sub, keys }) => (
            <div key={id} data-testid={id} className="bg-white rounded-2xl border border-[#EBDDD3] shadow-sm overflow-hidden">
              <div className="px-6 py-5 flex items-center gap-3 text-white" style={{ background: color }}>
                <span className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                <div><div className="font-display text-xl font-black leading-tight">{title}</div><div className="text-sm text-white/85">{sub}</div></div>
              </div>
              <ul className="p-6 space-y-5">
                {keys.map((k) => (
                  <li key={k} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
                    <div><div className="font-semibold text-[#2D231F]">{t(`fair.${k}t`)}</div><p className="text-sm text-[#6B5A50] leading-relaxed mt-0.5">{t(`fair.${k}d`)}</p></div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm font-semibold text-[#5A4B45]">{t("fair.foot")}</p>
      </section>

      {/* Featured craftsmen */}
      <section className="bg-[#EEF0E4] border-y border-[#DDE1CB]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#C65338] bg-[#C65338]/10 rounded-full px-3.5 py-1.5 mb-4">
              <ShieldCheck className="w-4 h-4" /> {t("craftsmen.eyebrow")}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F]">{t("craftsmen.title")}</h2>
            <p className="text-[#8C7A70] mt-2">{t("craftsmen.subtitle")}</p>
          </div>
          <Button variant="ghost" data-testid="see-all-craftsmen" onClick={() => nav("/craftsmen")} className="rounded-full gap-1.5 hidden sm:flex">
            {t("nav.findCraftsmen")} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((c, i) => <CraftsmanCard key={c._id} c={c} index={i} />)}
        </div>
      </div>
      </section>

      {/* Craftsman recruiting */}
      {!(user && user !== false) && (
      <section className="bg-[#FBEDCB] border-y border-[#F1D9A0]" data-testid="recruit-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <img src="/recruit-craftsman.jpg" alt="" loading="lazy" className="w-full h-64 md:h-80 object-cover object-center rounded-xl shadow-md" />
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F] leading-tight">{t("recruit.title")}</h2>
            <p className="font-display text-xl sm:text-2xl font-bold text-[#B36B00] mt-2">{t("recruit.sub")}</p>
            <p className="mt-5 text-[#5A4B45] leading-relaxed">{t("recruit.body")}</p>
            <button data-testid="recruit-cta" onClick={() => nav("/register?role=craftsman")} className="mt-6 inline-flex items-center gap-3 text-[#2D231F] font-semibold hover:text-[#B36B00] transition-colors">
              <span className="w-8 h-8 rounded-full bg-[#1E2B4A] text-white flex items-center justify-center"><ArrowRight className="w-4 h-4 rtl:rotate-180" /></span>
              <span className="underline underline-offset-4">{t("recruit.cta")}</span>
            </button>
          </div>
        </div>
      </section>
      )}

      <Pricing />
      <LegalNotice />
    </div>
  );
}